<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    /** 控制弹窗是否显示 */
    modelValue: boolean
    /** 预览的图片列表（可传入单张图片地址 string，或图片数组 string[]） */
    images?: string | string[]
    /** 初始预览的图片索引，默认 0 */
    initialIndex?: number
  }>(),
  {
    images: () => [],
    initialIndex: 0,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  close: []
}>()

// 统一将 images 规范为数组
const imageList = computed(() => {
  if (Array.isArray(props.images)) {
    return props.images.filter((img: any) => typeof img === 'string' && img.trim() !== '')
  }
  if (typeof props.images === 'string' && props.images.trim() !== '') {
    return [props.images]
  }
  return []
})

const currentIndex = ref(0)
const dialogRef = ref<HTMLElement | null>(null)

// 用于存储当前图片渲染的受控尺寸，确保 CommonImage 容器与内部图片保持一致。
const imgSize = ref<{ width?: string; height?: string }>({})
const imgSizeStyle = ref<Record<string, string>>({ opacity: '0' })

/** 计算图片比例并设置精确宽高样式 */
const handleImageLoad = (event: Event) => {
  const img = event.target as HTMLImageElement
  if (!img) return

  const naturalWidth = img.naturalWidth
  const naturalHeight = img.naturalHeight
  if (!naturalWidth || !naturalHeight) return

  // 设定留白极限边界：左右 120px (总宽-240px)，上下 50px (总高-100px)
  const limitWidth = Math.max(window.innerWidth - 240, 0)
  const limitHeight = Math.max(window.innerHeight - 100, 0)
  const scale = Math.min(limitWidth / naturalWidth, limitHeight / naturalHeight)

  imgSize.value = {
    width: `${naturalWidth * scale}px`,
    height: `${naturalHeight * scale}px`,
  }
  imgSizeStyle.value = { opacity: '1' }
}

/** 监听窗口大小变化以重新判定比例 */
const handleResize = () => {
  const imgEl = dialogRef.value?.querySelector('.preview-image .main-image') as HTMLImageElement
  if (imgEl && imgEl.complete) {
    handleImageLoad({ target: imgEl } as any)
  }
}

/** 核心逻辑：确保检测并应用图片尺寸自适应，解决缓存/网络加载时的时序问题 */
const checkAndRunImageLoad = () => {
  const imgEl = dialogRef.value?.querySelector('.preview-image .main-image') as HTMLImageElement
  if (!imgEl) return

  if (imgEl.naturalWidth > 0) {
    handleImageLoad({ target: imgEl } as any)
  } else {
    // 绑定 onload 兜底
    imgEl.addEventListener('load', (e) => handleImageLoad(e))

    // 极速轮询兜底，防缓存下不触发 onload 且第一帧 naturalWidth 仍为 0 的情况
    let count = 0
    const check = () => {
      if (imgEl.naturalWidth > 0) {
        handleImageLoad({ target: imgEl } as any)
      } else if (count < 30 && props.modelValue) {
        count++
        requestAnimationFrame(check)
      }
    }
    requestAnimationFrame(check)
  }
}

/** 切换到上一张 */
const prevImage = () => {
  if (imageList.value.length <= 1 || currentIndex.value === 0) return
  currentIndex.value -= 1
}

/** 切换到下一张 */
const nextImage = () => {
  if (imageList.value.length <= 1 || currentIndex.value === imageList.value.length - 1) return
  currentIndex.value += 1
}

/** 关闭弹层 */
const closeDialog = () => {
  emit('update:modelValue', false)
  emit('close')
}

/** 监听键盘快捷操作（声明提升至 watch 之前以防 TDZ 时序引用问题） */
const handleKeyDown = (event: KeyboardEvent) => {
  if (!props.modelValue) return

  if (event.key === 'Escape') {
    closeDialog()
  } else if (event.key === 'ArrowLeft' || event.key === 'Left') {
    prevImage()
  } else if (event.key === 'ArrowRight' || event.key === 'Right') {
    nextImage()
  }
}

// 当 modelValue 改变时同步初始化 index并处理尺寸自适应重算，在捕获阶段(true)注册键盘监听以防事件拦截
watch(
  () => props.modelValue,
  async (value: any) => {
    if (!import.meta.client) return

    if (!value) {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('keydown', handleKeyDown, true)
      imgSizeStyle.value = { opacity: '0' }
      imgSize.value = {}
      return
    }

    // 打开时，重置当前索引为 initialIndex，并将初始状态置为不可见防闪烁
    imgSizeStyle.value = { opacity: '0' }
    imgSize.value = {}
    currentIndex.value = props.initialIndex >= 0 && props.initialIndex < imageList.value.length ? props.initialIndex : 0

    window.addEventListener('resize', handleResize)
    window.addEventListener('keydown', handleKeyDown, true) // 使用 true 捕获模式监听，防止被 ElDialog 吞掉

    await nextTick()
    dialogRef.value?.focus()

    // 首次打开时，立即调用一次重算
    checkAndRunImageLoad()
    // 额外在淡入动画极早期（50ms后）强行再检测重算一次，防止 Transition 期间 CSS 渲染被丢弃
    setTimeout(checkAndRunImageLoad, 50)
  },
  { immediate: true },
)

// 切换图片时同样强制重算以应对缓存大图不触发 @load 的问题
watch(
  () => currentIndex.value,
  async () => {
    // 保留上一张图片的可见状态和尺寸，避免缓存命中时加载事件早于状态恢复而永久透明。
    await nextTick()
    checkAndRunImageLoad()
    // 切换后 50ms 同样追加一次安全兜底重算
    setTimeout(checkAndRunImageLoad, 50)
  },
)

onBeforeUnmount(() => {
  if (import.meta.client) {
    window.removeEventListener('resize', handleResize)
    window.removeEventListener('keydown', handleKeyDown, true) // 卸载捕获模式的监听
  }
})
</script>

<template>
  <ElDialog
    :model-value="modelValue"
    fullscreen
    append-to-body
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    class="common-image-preview-dialog"
    modal-class="common-image-preview-dialog-mask"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div ref="dialogRef" class="group relative flex h-full w-full items-center justify-center outline-none">
      <!-- 右上角关闭按钮 -->
      <button
        type="button"
        class="absolute top-[40px] right-[40px] flex h-[32px] w-[32px] cursor-pointer items-center justify-center rounded-full bg-[rgba(255,255,255,0.08)] text-[#fff] transition-all duration-[200ms] rtl:right-auto rtl:left-[40px]"
        aria-label="关闭图片预览"
        @click="closeDialog"
      >
        <CommonImage
          src="https://resources1.interface003.com/web30/2026/07/07/6067ca80-b0cc-4c62-9875-771f3d1a6cd3.png"
        />
      </button>
      <div>{{ imageList.length }}--{{ currentIndex }}</div>

      <!-- 左切换按钮（多图时展示） -->
      <CommonImage
        v-if="imageList.length > 1 && currentIndex > 0"
        src="https://resources1.interface003.com/web30/2026/07/07/06965481-a550-44f3-bdad-791fb3d0f436.png"
        class="pointer-events-none absolute left-[40px] z-40 cursor-pointer object-contain opacity-0 transition-all duration-300 group-hover:pointer-events-auto group-hover:opacity-100 rtl:right-[40px] rtl:left-auto rtl:rotate-180"
        width="40"
        height="40"
        @click="prevImage"
      />

      <!-- 主图片展示区域，限制左右最大留白 120px (等效宽-240px)，上下最大留白 50px (等效高-100px)，等比自适应且防拉伸 -->
      <div class="pointer-events-none relative flex items-center justify-center">
        <CommonImage
          class="preview-image pointer-events-auto object-contain transition-all duration-300 select-none"
          :src="imageList[currentIndex]"
          :width="imgSize.width"
          :height="imgSize.height"
          :style="imgSizeStyle"
          loading="eager"
          alt=""
          @load="handleImageLoad"
        />
      </div>

      <!-- 右切换按钮（多图时展示） -->
      <CommonImage
        v-if="imageList.length > 1 && currentIndex < imageList.length - 1"
        src="https://resources1.interface003.com/web30/2026/07/07/06965481-a550-44f3-bdad-791fb3d0f436.png"
        class="pointer-events-none absolute right-[40px] z-40 rotate-180 cursor-pointer object-contain opacity-0 transition-all duration-300 group-hover:pointer-events-auto group-hover:opacity-100 rtl:right-auto rtl:left-[40px] rtl:rotate-0"
        width="40"
        height="40"
        @click="nextImage"
      />
    </div>
  </ElDialog>
</template>

<style scoped>
:global(.common-image-preview-dialog.el-dialog) {
  overflow: visible;
  border: 0;
  background: transparent;
  box-shadow: none;
  padding: 0;
}

:global(.common-image-preview-dialog .el-dialog__header) {
  display: none;
  padding: 0;
  margin: 0;
}

:global(.common-image-preview-dialog .el-dialog__body) {
  height: 100%;
  overflow: visible;
  padding: 0;
}

:global(.common-image-preview-dialog-mask) {
  background: rgb(5 12 31 / 68%);
}
</style>
